import { Component, OnDestroy, OnInit, OnChanges, Input, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { TileParent, TileVM } from '@/core/models/tile-vm';
import { TileVmMapper } from '@/core/models/tile-vm-mapper';
import { TileType } from '@/core/models/tile-type';
import { TileService } from '@/core/services/open-api/tile.service';
import { Tile } from '@/core/models/tile';
import { TranslateService } from '@ngx-translate/core';
import { TilesComponent } from '../tiles.component';
import { AssignmentOptions } from '@/core/models/assignment-options.interface';
import { User } from '@/core/models/user';
import { Group } from '@/core/models/group.interface';
import { ConfirmationService } from 'primeng/api';
import { GroupForm } from '@/core/models/group-form';
import { InitializationManager } from '@/core/utils/initialization-manager';
import { LoadingService } from '@/core/services/loading.service';
import { UserService } from '@/core/services/open-api/user.service';
import { GroupService } from '@/core/services/open-api/group.service';
import { firstValueFrom, Observable, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { AngularStyle, AngularTranslations } from '@/core/types/angular-primeng-api';
import { ToastService } from '@/core/services/toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Popover } from 'primeng/popover';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ColorPickerChangeEvent } from 'primeng/colorpicker';
import { ColorService } from '@/core/services/color.service';
import { AvailableIntegrations } from '@/core/models/IntegrationEntity';
import { TimeBudget, TimeBudgetErrorCode } from '@/core/models/time-budget';
import { TimeBudgetService } from '@/core/services/open-api/time-budget.service';
import { TimeBudgetUtils } from '@/core/utils/time-budget-utils';
import { ApiErrorHandlerService } from '@/core/services/api-error-handler.service';
import { TypeGuard } from '@/core/types/type-guard';

export interface TeamMember {
    id: number;
    name: string;
    role: string;
    firstName?: string; // added, were missing in team-members.interface.ts
    lastName?: string; // added, were missing in team-members.interface.ts
}

type AssignmentTypeTeamMember = {
    type: 'members';
} & TeamMember;
type AssignmentTypeGroup = {
    type: 'groups';
} & Group;

// saves the budget settings of a tile once the tile is saved (a new tile only gets its id then)
type TimeBudgetRequest = (tileId: number) => Observable<TimeBudget>;

const _toMember = function (assignment: AssignmentTypeTeamMember): TeamMember {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { type, ...member } = assignment;
    return member;
};

const _toMemberOption = function (member: TeamMember): AssignmentTypeTeamMember {
    return {
        type: 'members',
        ...member,
    };
};

const _toGroup = function (assignment: AssignmentTypeGroup): Group {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { type, ...group } = assignment;
    return group;
};

const _toGroupOption = function (group: Group): AssignmentTypeGroup {
    return {
        type: 'groups',
        ...group,
    };
};

const _toUser = function (teamMember: TeamMember): User {
    // @ts-expect-error -- TODO legacy logic; needs to be fixed
    return teamMember;
};

const _userToTeamMember = function (user: User): TeamMember {
    return {
        id: user.id,
        name: user.firstName + ' ' + user.lastName,
        role: user.getRoleString(),
        firstName: user.firstName,
        lastName: user.lastName,
    };
};

const _toGroups = function (groupEntities: GroupForm[]): Group[] {
    // TODO legacy logic; needs to be fixed
    return groupEntities as Group[];
};

@Component({
    selector: 'app-tile-edit-dialog',
    templateUrl: './tile-edit-dialog.component.html',
    styleUrls: ['./tile-edit-dialog.component.scss'],
    standalone: false,
})
export class TileEditDialogComponent implements OnInit, OnDestroy, OnChanges {
    @Input() showAddEditDialog = false;
    @Input() tile?: TileVM;
    @Input() isPro: boolean = false;
    @Input() showArchived: boolean = false;
    @Input() layerName: string = '';
    @Input() layerId: number = 0;
    @Input() layerIndex: number = 0;
    @Input() parentLayerName: string = '';
    @Input() hasParent: boolean = false;
    @Input() tileParents: TileParent[] = [];
    @Output() onTileChanged = new EventEmitter<Tile>();
    @Output() onTileAdded = new EventEmitter<Tile>();
    @Output() onTileDeleted = new EventEmitter<Tile>();
    @Output() onClose = new EventEmitter<void>();
    @Output() onTimeBudgetChanged = new EventEmitter<TimeBudget>();
    // internal show state:
    showDialog: boolean = false;
    // adding a new tile (true) or editing an existing one?
    isAddCase: boolean = false;
    // self calculated:
    loading: boolean = false;
    isTileArchived: boolean = false;
    tileHasActivities: boolean = false;
    isAssignedToAllUsers: boolean = false;
    teamMembers: TeamMember[] = [];
    selectedAssignments: Array<AssignmentTypeTeamMember | AssignmentTypeGroup> = [];
    assignmentOptions: AssignmentOptions<AssignmentTypeGroup | AssignmentTypeTeamMember>[] = [];
    allGroups: Group[] = [];
    colors: string[];
    colorForm: FormGroup;
    nameInvalid: boolean = false;
    // time budget of the tile (switched on or off), if it has one:
    timeBudget?: TimeBudget;
    canReadTimeBudgets: boolean = false;
    timeBudgetEnabled: boolean = false;
    timeBudgetHours: number | null = null;
    timeBudgetLoading: boolean = false;
    timeBudgetLoadFailed: boolean = false;
    // another admin set a budget on the tile while this dialog was open:
    timeBudgetConflict: boolean = false;
    readonly maxTimeBudgetHours = TimeBudgetUtils.MAX_HOURS;
    readonly timeBudgetFractionDigits = TimeBudgetUtils.HOURS_FRACTION_DIGITS;
    private timeBudgetLoad?: Subscription;
    // whether the tile could have a budget when the dialog opened, i.e. before parent changes made in it
    private timeBudgetAllowedOnOpen = false;
    // counts the openings of the dialog, so that a save finishing late does not act on a later opening
    private dialogSession = 0;
    // initMgr:
    private initMgr: InitializationManager;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly confirmationService: ConfirmationService,
        private readonly translate: TranslateService,
        private readonly loadingService: LoadingService,
        private readonly userService: UserService,
        private readonly groupService: GroupService,
        private readonly tileService: TileService,
        private readonly toastService: ToastService,
        private readonly colorService: ColorService,
        private readonly timeBudgetService: TimeBudgetService,
        private readonly apiErrorHandler: ApiErrorHandlerService,
        private fb: FormBuilder,
    ) {
        this.initMgr = new InitializationManager(this.loadingService, this.constructor.name);
        this.colors = this.colorService.getTileColorPalette();
        this.colorForm = this.fb.group({
            color: ['', [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)]],
        });
    }

    public ngOnInit(): void {
        this.initMgr
            .loadingAnimation(false)
            .addDependency(this.userService.account$, {
                name: 'account$',
                callback: (account) => {
                    this.teamMembers = account.getUsers().map((user: User) => _userToTeamMember(user));
                },
            })
            .addDependency(this.groupService.getAllGroups())
            .addDependency(this.translate.get(['member_option', 'group_option']))
            .onInit(async ([_account, groups, translations]) => {
                this.allGroups = _toGroups(groups);
                this.populateAssignmentOptions(this.allGroups, this.teamMembers, translations);
            })
            .onChange(async ([_account, _groups, translations]) => {
                this.populateAssignmentOptions(this.allGroups, this.teamMembers, translations);
            })
            .initialize();

        this.timeBudgetService.canRead$.pipe(takeUntil(this.destroy$)).subscribe((canRead) => {
            this.canReadTimeBudgets = canRead;
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['showAddEditDialog']) {
            if (this.tile === undefined) {
                if (changes['tile']) {
                    this.tile = changes['tile'].currentValue;
                } else {
                    console.error('TileEditDialogComponent: Aborted opening dialog because tile is not set.');
                    return;
                }
            }
            const val = changes['showAddEditDialog'].currentValue;
            if (val === true) {
                void this.openDialog();
            } else if (val === false) {
                this.closeDialogByInput();
            }
        }
    }

    private async openDialog(): Promise<void> {
        if (!this.tile) {
            return;
        }
        this.dialogSession++;
        this.isAddCase = this.tile.id === 0;
        this.resetTimeBudget();
        this.timeBudgetAllowedOnOpen = this.isTimeBudgetAllowed();
        if (this.isAddCase) {
            this.isAssignedToAllUsers = this.tile.isAvailableToAllUsers;
            this.selectedAssignments = [];
            this.isTileArchived = false;
        } else {
            this.loadTimeBudget(this.tile.id);
            await this.initForEdit(this.tile);
        }
        this.showDialog = true;
    }

    private closeDialogByInput(): void {
        this.showDialog = false;
        this.setLoading(false);
    }

    public closeDialog(): void {
        this.showDialog = false;
        // this.showAddEditDialog = false;
        this.setLoading(false);
        this.onClose.emit();
    }

    private async initForEdit(tileVM: TileVM): Promise<void> {
        // onRowSelect
        this.isTileArchived = tileVM.archived === true;
        // Ensure tileParents includes archived tiles when editing
        // this.filterTileParents(true);
        // Only archived tiles can be deleted, thus check is only run for archived tiles
        if (this.isTileArchived) {
            this.loading = true;
            this.tileHasActivities = await firstValueFrom(this.tileService.checkTilesHaveActivities([tileVM.id]));
            this.loading = false;
        }
        // sets the isAssignedToAllUsers flag based on the tile data
        this.isAssignedToAllUsers = tileVM.isAvailableToAllUsers ?? false;

        const selectedMembers = this.teamMembers.filter((member: TeamMember) => {
            return tileVM!.assignedUsers.some((assignedUser: User) => assignedUser.id === member.id);
        });

        // check if the selected tile is preset in any of the groups.tiles -> tile.id
        const assignedGroupsForSelectedTile = this.allGroups.filter((group: Group) =>
            group.tiles.some((tile: Tile) => tile.id === tileVM.id),
        );

        const structuredTeamAssignments: AssignmentTypeTeamMember[] = selectedMembers.map((member: TeamMember) =>
            _toMemberOption(member),
        );

        const structuredGroupAssignments: AssignmentTypeGroup[] = assignedGroupsForSelectedTile.map((group: Group) =>
            _toGroupOption(group),
        );

        this.selectedAssignments = [...structuredTeamAssignments, ...structuredGroupAssignments];
    }

    public onSingleTileArchiveOrRestore(): void {
        const tileName = this.tile?.name || '';
        const header = this.translate
            .instant(!this.showArchived ? 'confirm_archive_user_header' : 'confirm_unarchive_user_header')
            .replace('{{tileName}}', tileName)
            .replace('{{tileCount}}', '');
        this.confirmationService.confirm({
            message: !this.showArchived
                ? this.translate
                      .instant('confirm_tile_archive')
                      .replace('{{tileName}}', this.tile?.name)
                      .replace('{{layerName}}', this.layerName)
                : this.translate
                      .instant('confirm_tile_restore')
                      .replace('{{tileName}}', this.tile?.name)
                      .replace('{{layerName}}', this.layerName),
            key: 'archiveTile',
            header: header,
            icon: 'pi pi-info-circle',
            acceptLabel: !this.showArchived
                ? this.translate.instant('tiles_archive')
                : this.translate.instant('tiles_restore'),
            rejectLabel: this.translate.instant('button_cancel'),
            acceptButtonStyleClass: 'p-button-primary',
            rejectButtonStyleClass: 'p-button-text p-button-primary',
            acceptIcon: 'none',
            rejectIcon: 'none',
            accept: () => {
                this.setLoading(true);

                if (this.tile && this.tile?.id !== 0) {
                    const dto = TileVmMapper.mapToDto(this.tile);
                    dto.archived = !dto.archived;
                    this.tileService
                        .update(dto)
                        .pipe(
                            tap(() => {
                                this.onTileChanged.emit(dto);
                                this.closeAfterSuccess();
                            }),
                        )
                        .subscribe();
                    TilesComponent.showArchiveOpToast(this.toastService, this.showArchived);
                }
            },
        });
    }

    public saveClicked(): void {
        if (!this.tile) {
            return;
        }
        this.save(this.tile);
    }

    public isSaveDisabled(): boolean {
        if (!this.tile) {
            return true;
        }

        return (
            this.tile.name.length === 0 ||
            this.nameInvalid ||
            this.isTimeBudgetInvalid() ||
            (this.hasParent && this.tile.type === TileType.Normal && this.tile.parents.length === 0) ||
            (this.tileParents.length == 0 && this.tile.type !== TileType.Global && this.layerIndex !== 1) ||
            (this.tileParents.length !== 0 &&
                this.tile.type !== TileType.Global &&
                this.layerIndex !== 1 &&
                this.tile.parents.length === 0)
        );
    }

    private save(tile: TileVM): void {
        this.setLoading(true);
        const saveTimeBudget = this.getTimeBudgetRequest();
        const session = this.dialogSession;

        // Set the isAvailableToAllUsers flag based on the isAssignedToAllUsers condition
        tile.isAvailableToAllUsers = this.isAssignedToAllUsers === true;
        tile.assignedGroups = this.selectedAssignments
            .filter((a) => a.type === 'groups')
            .map((a) => _toGroup(a as AssignmentTypeGroup));
        tile.assignedUsers = this.selectedAssignments
            .filter((a) => a.type === 'members')
            .map((a) => _toUser(_toMember(a as AssignmentTypeTeamMember)));
        // Map the tile to a DTO
        const dto = TileVmMapper.mapToDto(tile);

        if (tile.id !== 0) {
            // Logic for handling tile updates
            const updateTileDetails = (): void => {
                this.groupService.getAllGroups().subscribe((groups: GroupForm[]) => {
                    this.allGroups = _toGroups(groups);
                    this.translate
                        .get(['member_option', 'group_option'])
                        .subscribe((transl: Record<string, string>) => {
                            this.populateAssignmentOptions(this.allGroups, this.teamMembers, transl);
                        });
                });
            };

            // TODO refactor this not DRY code...
            if (tile.isAvailableToAllUsers === false) {
                // handle the case where the tile is being modified and is private
                this.tileService.update(dto).subscribe({
                    next: (response: Tile) => {
                        console.log('Tile updated successfully with a response: ', response);
                        // TODO
                        // this.updatePage(true);
                        updateTileDetails();
                        this.toastService.success('standard_success', 'standard_success_saved');
                        this.onTileChanged.emit(response);
                        this.saveTimeBudgetAndClose(tile.id, saveTimeBudget, session);
                    },
                    error: (error: HttpErrorResponse) => this.handleError(error, 'updating the tile'),
                });
            } else {
                // handle the case where the tile is being modified and is being turned public
                this.tileService.update(dto).subscribe({
                    next: (response: Tile) => {
                        this.onTileChanged.emit(response);
                        updateTileDetails();
                        this.toastService.success('standard_success', 'standard_success_saved');
                        this.saveTimeBudgetAndClose(tile.id, saveTimeBudget, session);
                    },
                    error: (error: HttpErrorResponse) => this.handleError(error, 'setting the tile to all users'),
                });
            }
        } else {
            // Logic for handling new tile creation
            const assignedUserIds = this.selectedAssignments
                .map((a) => (a.type === 'members' ? a.id : null))
                .filter((id) => id !== null) as number[];

            const assignedGroupIds = this.selectedAssignments
                .map((a) => (a.type === 'groups' ? a.id : null))
                .filter((id) => id !== null) as number[];

            dto.layer = this.layerId;

            this.tileService.create(dto, assignedUserIds ?? null, assignedGroupIds ?? null).subscribe({
                next: (response: Tile) => {
                    this.allGroups = this.allGroups.map((group: Group) => {
                        response.assignedGroups.forEach((assignedGroup: Group) => {
                            if (group.id === assignedGroup.id) {
                                group.tiles.push(response);
                            }
                        });
                        return group;
                    });

                    dto.id = response.id;

                    this.toastService.success('standard_success', 'standard_success_saved');
                    this.onTileAdded.emit(dto);
                    this.saveTimeBudgetAndClose(response.id, saveTimeBudget, session);
                },
                error: (error: HttpErrorResponse) => this.handleError(error, 'setting the tile to all users'),
            });
        }
    }

    public getArchiveButtonLabel(): string {
        let result: string = '';
        this.translate.get(['tiles_restore', 'tiles_archive']).subscribe((translations) => {
            result = this.showArchived ? translations.tiles_restore : translations.tiles_archive;
        });
        return result;
    }

    public deleteTileConfirm(): void {
        TilesComponent.showConfirmDeleteDialog(this.confirmationService, this.translate, this.tile?.name || '');
    }

    public isPublicSwitchDisabled(): boolean {
        const isUserPro = this.isPro;
        const isSelTilePublic = this.tile?.isAvailableToAllUsers;
        return !!(isSelTilePublic && !isUserPro);
    }

    public isGlobal(): boolean {
        if (!this.tile) {
            return false;
        }

        return this.tile.type === TileType.Global;
    }

    public changeParents(tileParents: TileParent[]): void {
        if (!this.tile) {
            return;
        }
        this.tile.parents = JSON.parse(JSON.stringify(tileParents));
    }

    public changeGlobal(): void {
        if (!this.tile) {
            return;
        }
        this.tile.type = this.tile.type === TileType.Global ? TileType.Normal : TileType.Global;
    }

    isInvalidColor(): boolean | undefined {
        return this.colorForm.get('color')?.invalid && this.colorForm.get('color')?.touched;
    }

    onColorInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const hexValue = input.value;
        const hexPattern = /^#[0-9A-Fa-f]{0,6}$/;
        if (hexPattern.test(hexValue)) {
            this.colorForm.get('color')?.setValue(hexValue, { emitEvent: false });
            if (this.tile) {
                this.tile.color = hexValue;
            }
        }
    }

    initializeColorPickerAndInput(): void {
        this.colorForm.get('color')?.markAsTouched();
        if (this.tile) {
            const currentColor = this.tile.color || '#FFFFFF';
            this.colorForm.get('color')?.setValue(currentColor, { emitEvent: false });
        }
    }

    onCustomColorChange(event: ColorPickerChangeEvent): void {
        if (!this.tile) {
            return;
        }
        this.tile.color = event.value as string; // TODO FIXME check if this works

        if (event?.value) {
            this.colorForm.get('color')?.setValue(event.value, { emitEvent: false });
        }
    }

    onSubmit(overlay: Popover): void {
        if (!this.tile) {
            return;
        }
        if (this.colorForm.valid) {
            const hexColor = this.colorForm.get('color')?.value;
            overlay.hide();
            this.tile.color = hexColor;
        }
    }

    changeColor(color: string): void {
        if (!this.tile) {
            return;
        }
        // custom color picker default value update
        this.tile.color = color;
        this.colorForm.get('color')?.setValue(color, { emitEvent: false });
    }

    toggleFavorite(): void {
        if (!this.tile) {
            return;
        }
        this.tile.favorite = !this.tile.favorite;
    }

    setCustomColorpickerStyle(): AngularStyle | undefined {
        if (!this.tile) {
            return;
        }

        return {
            background: this.tile.color,
            border: this.tile.color.toLowerCase() === '#ffffff' ? '1px solid #bbb' : '',
        };
    }

    // only admins and managers open the dialog, and they may change the budgets they see
    public isTimeBudgetVisible(): boolean {
        return this.canReadTimeBudgets && this.tile !== undefined && this.tile.type !== TileType.Absence;
    }

    // free accounts see a budget set earlier (e.g. during the trial) but can't change it
    public isTimeBudgetDisabled(): boolean {
        return !this.isPro || this.isTileArchived || this.timeBudgetLoading || this.timeBudgetLoadFailed;
    }

    public isTimeBudgetInvalid(): boolean {
        return (
            this.isTimeBudgetVisible() &&
            this.isTimeBudgetAllowed() &&
            this.timeBudgetEnabled &&
            !TimeBudgetUtils.isValidHours(this.timeBudgetHours)
        );
    }

    // an empty field only keeps Save disabled; an entered amount that can't be saved is marked
    public isTimeBudgetHoursMarkedInvalid(): boolean {
        return this.timeBudgetHours !== null && this.isTimeBudgetInvalid();
    }

    /** The translation key of the switch's tooltip, when it is disabled for a reason the user can act on. */
    public getTimeBudgetTooltip(): string | undefined {
        if (!this.isPro) {
            return 'time_budget_proFeature';
        }
        if (this.isTimeBudgetAllowed()) {
            return undefined;
        }
        // a global tile counts as assigned to every parent
        return this.tile?.type !== TileType.Global && this.tile?.parents.length === 0
            ? 'time_budget_unavailable_no_parent'
            : 'time_budget_unavailable_several_parents';
    }

    // follows the parents and the global switch as they are changed in the dialog
    public isTimeBudgetAllowed(): boolean {
        return this.tile !== undefined && TimeBudgetUtils.isAllowedFor(this.tile, this.layerIndex);
    }

    // the tile's budget is on, and the tile gets several parents or becomes global in this dialog
    public isTimeBudgetSwitchedOffOnSave(): boolean {
        return (
            this.timeBudgetAllowedOnOpen &&
            !this.isTimeBudgetAllowed() &&
            !this.isTimeBudgetDisabled() &&
            this.timeBudget?.active === true
        );
    }

    // the app's language, as in the budget column, not the browser's
    public getTimeBudgetLocale(): string {
        return this.translate.getCurrentLang() || 'en';
    }

    private resetTimeBudget(): void {
        this.timeBudgetLoad?.unsubscribe(); // a load of an earlier opening must not overwrite this one
        this.timeBudget = undefined;
        this.timeBudgetEnabled = false;
        this.timeBudgetHours = null;
        this.timeBudgetLoading = false;
        this.timeBudgetLoadFailed = false;
        this.timeBudgetConflict = false;
    }

    /** Loads the tile's budget, switched on or off. With keepInput, the dialog keeps what was entered. */
    private loadTimeBudget(tileId: number, keepInput: boolean = false): void {
        if (!this.isTimeBudgetVisible()) {
            return;
        }
        this.timeBudgetLoad?.unsubscribe();
        this.timeBudgetLoading = true;
        this.timeBudgetLoad = this.timeBudgetService
            .list([tileId])
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (budgets: TimeBudget[]) => {
                    if (this.tile?.id !== tileId) {
                        return; // the dialog shows another tile by now
                    }
                    this.timeBudget = budgets.find((budget) => budget.tileId === tileId);
                    if (this.timeBudget && !keepInput) {
                        this.timeBudgetEnabled = this.timeBudget.active;
                        this.timeBudgetHours = TimeBudgetUtils.secondsToHours(this.timeBudget.budgetSeconds);
                    }
                    this.timeBudgetLoading = false;
                },
                error: () => {
                    if (this.tile?.id !== tileId) {
                        return;
                    }
                    this.timeBudgetLoading = false;
                    this.timeBudgetLoadFailed = true;
                },
            });
    }

    /** Saves the budget once the tile is saved, then closes the dialog. A failed budget save does not undo the tile. */
    private saveTimeBudgetAndClose(
        tileId: number,
        saveTimeBudget: TimeBudgetRequest | undefined,
        session: number,
    ): void {
        if (saveTimeBudget === undefined) {
            this.closeAfterSave(session);
            return;
        }
        saveTimeBudget(tileId).subscribe({
            next: (budget: TimeBudget) => {
                this.onTimeBudgetChanged.emit(budget);
                this.closeAfterSave(session);
            },
            error: (error: unknown) => this.onTimeBudgetSaveError(error, tileId, session),
        });
    }

    /** Decided when Save is clicked: the dialog can be closed and opened for another tile before the tile is saved. */
    private getTimeBudgetRequest(): TimeBudgetRequest | undefined {
        if (!this.isTimeBudgetVisible() || this.isTimeBudgetDisabled()) {
            return undefined;
        }
        const timeBudgetId = this.timeBudget?.id;
        if (!this.isTimeBudgetAllowed()) {
            // kept switched off with its hours, for when the tile has one parent again; the budget of a tile that was
            // global or had several parents when the dialog opened (the API allows that) is left as it is
            if (this.isTimeBudgetSwitchedOffOnSave() && timeBudgetId !== undefined) {
                return () => this.timeBudgetService.update(timeBudgetId, { active: false });
            }
            return undefined;
        }
        const change = TimeBudgetUtils.getChange(this.timeBudget, this.timeBudgetEnabled, this.timeBudgetHours);
        if (change.type === 'create') {
            return (tileId) => this.timeBudgetService.create(tileId, change.budgetSeconds);
        }
        if (change.type === 'update' && timeBudgetId !== undefined) {
            return () => this.timeBudgetService.update(timeBudgetId, change.changes);
        }
        return undefined;
    }

    private onTimeBudgetSaveError(error: unknown, tileId: number, session: number): void {
        const code = TimeBudgetService.getErrorCode(error);
        if (session === this.dialogSession && code === TimeBudgetErrorCode.TileAlreadyHasBudget && !this.isAddCase) {
            // someone set a budget on this tile meanwhile: show it, so that it can be changed or switched on
            this.setLoading(false);
            this.timeBudgetConflict = true;
            this.loadTimeBudget(tileId, true);
            return;
        }
        // the error interceptor already shows a message for known API errors
        if (!(TypeGuard.isHttpApiError(error) && this.apiErrorHandler.canHandle(error.error.error))) {
            this.toastService.error('standard_error', 'time_budget_save_failed');
        }
        this.closeAfterSave(session);
    }

    /** Closes the dialog after saving, unless it was opened again meanwhile, maybe for another tile. */
    private closeAfterSave(session: number): void {
        if (session === this.dialogSession) {
            this.closeAfterSuccess();
        } else {
            this.onTilesModified();
        }
    }

    private populateAssignmentOptions(groups: Group[], members: TeamMember[], transl: AngularTranslations): void {
        this.assignmentOptions = [
            {
                label: transl['group_option'],
                value: 'groups',
                items: groups.map((group: Group) => ({
                    label: group.name,
                    value: _toGroupOption(group),
                })),
            },
            {
                label: transl['member_option'],
                value: 'members',
                items: members.map((member: TeamMember) => ({
                    label: `${member.firstName} ${member.lastName}`,
                    value: _toMemberOption(member),
                })),
            },
        ];
    }

    private setLoading(loading: boolean): void {
        if (this.loading === loading) {
            return;
        }
        this.loadingService.setLoading(loading, this.constructor.name);
        this.loading = loading;
    }

    private onTilesModified(): void {
        // invalidate the tile cache, so that on the next subscription to tiles$ the tiles are fetched again from the api
        this.tileService.invalidateCache();
    }

    private closeAfterSuccess(): void {
        this.onTilesModified();
        this.closeDialog();
    }

    private handleError(error: HttpErrorResponse, context: string): void {
        this.toastService.error('standard_error');
        console.error(`An error occurred in ${context}:`, error);
        this.setLoading(false);
        this.loading = false;
        this.showAddEditDialog = false;
    }

    public openExternalResource(): void {
        if (!this.tile?.externalRefs) {
            return;
        }
        const externalRefs = this.tile.externalRefs;
        const externalRefKeys = Object.keys(externalRefs);
        if (externalRefKeys.length === 0) {
            return;
        }
        const externalRef = externalRefKeys[0] as AvailableIntegrations;
        const url = this.tile.externalRefs[externalRef]?.url_;
        if (!url) {
            return;
        }
        window.open(url, '_blank');
    }

    public getSelectedTileExternalRefNames(): string {
        return Object.keys(this.tile?.externalRefs ?? {}).join(', ');
    }

    public checkNameValidity(name: string): void {
        this.nameInvalid = name.length === 0 || name.length > 255;
    }
}
